import * as React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Alert } from "../types";
import { MouseEventHandler, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { mergeParams } from "../utils/url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport, faPlus } from "@fortawesome/free-solid-svg-icons";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  MenuListProps: {
    disablePadding: true,
  },
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 320,
      padding: 0,
    },
  },
};

type BaseAlertsPickerProps = {
  id: string;
  label: string;
  alerts: Alert[];
  width?: number | string;
  placeholder?: string;
};

type AlertsPickerMultiProps = BaseAlertsPickerProps & {
  multiple?: true;
  value: string[];
  onChange: (v: string[]) => void;
};

type AlertsPickerSingleProps = BaseAlertsPickerProps & {
  multiple: false;
  value: string;
  onChange: (v: string) => void;
};

type AlertsPickerProps = AlertsPickerMultiProps | AlertsPickerSingleProps;

export const AlertsPicker: React.FC<AlertsPickerProps> = (props) => {
  const { id, label, alerts, value } = props;
  const width = props.width ?? 340;
  const placeholder = props.placeholder ?? "Select an alert";
  const multiple = props.multiple !== false;

  const idToLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of alerts) {
      m.set(n.id, n.name);
    }
    return m;
  }, [alerts]);

  const selectedValues = useMemo(() => {
    if (Array.isArray(value)) {
      return value.filter((v) => v.length > 0);
    }
    if (typeof value === "string" && value.length > 0) {
      return [value];
    }
    return [];
  }, [value]);

  const handleChange = (
    event: SelectChangeEvent<string | string[]>,
  ) => {
    const v = event.target.value;
    if (props.multiple === false) {
      const next =
        typeof v === "string"
          ? v
          : Array.isArray(v)
            ? (v[0] ?? "")
            : "";
      props.onChange(next);
      return;
    }

    const next = (
      typeof v === "string"
        ? v.split(",")
        : Array.isArray(v)
          ? v
          : []
    ).filter((item) => item.length > 0);
    props.onChange(next);
  };

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const onNew: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${pathname}?${mergeParams(searchParams, new URLSearchParams({dialog: "new-alert"})).toString()}`);
  }

  const onImport: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`${pathname}?${mergeParams(searchParams, new URLSearchParams({dialog: "import-alerts"})).toString()}`);
  }

  return (
    <FormControl sx={{ m: 0, width }}>
      <FormLabel sx={{ mb: 0.75 }}>{label}</FormLabel>

      <Select
        id={`${id}-select`}
        multiple={multiple}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput />}
        displayEmpty={!multiple}
        renderValue={(selected) => (
          multiple ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {(Array.isArray(selected) ? selected : []).map((depId) => (
                <Chip
                  variant="filled"
                  color="primary"
                  key={depId}
                  label={idToLabel.get(depId) ?? depId}
                  size="small"
                />
              ))}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                color:
                  typeof selected === "string" && selected.length > 0
                    ? "text.primary"
                    : "text.secondary",
                fontStyle:
                  typeof selected === "string" && selected.length > 0
                    ? "normal"
                    : "italic",
              }}
            >
              {typeof selected === "string" && selected.length > 0
                ? (idToLabel.get(selected) ?? selected)
                : placeholder}
            </Box>
          )
        )}
        MenuProps={MenuProps}
      >
        <MenuItem key="import-alert" onClick={onImport}>
          <ListItemText onClick={onImport}>
            <Box display="flex" gap={1} alignItems="center">
              <FontAwesomeIcon icon={faFileImport} />
              Import
            </Box>
          </ListItemText>
        </MenuItem>
        <MenuItem key="new-alert" onClick={onNew}>
          <ListItemText onClick={onNew}>
            <Box display="flex" gap={1} alignItems="center">
              <FontAwesomeIcon icon={faPlus} />
              New
            </Box>
          </ListItemText>
        </MenuItem>
        {!multiple && (
          <MenuItem value="">
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {alerts.map((alert) => (
          <MenuItem key={alert.id} value={alert.id}>
            {multiple && <Checkbox checked={selectedValues.includes(alert.id)} />}
            <ListItemText primary={alert.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
